import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, PackageCheck } from 'lucide-react';
import { useBestellung } from '@/hooks/useBestellung';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useSerielleAktion } from '@/hooks/useSerielleAktion';
import { usePatchBestellung } from '@/hooks/usePatchBestellung';
import { useCreateSendung } from '@/hooks/useCreateSendung';
import { apiFetch, ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { DetailHeader } from '@/components/detail/DetailHeader';
import { ZustandSwitcher } from '@/components/detail/ZustandSwitcher';
import { AbholdatumEditor } from '@/components/detail/AbholdatumEditor';
import { GeschenkSchalter } from '@/components/detail/GeschenkSchalter';
import { LieferwunschSchalter } from '@/components/detail/LieferwunschSchalter';
import { WiedervorlageEditor } from '@/components/detail/WiedervorlageEditor';
import { VersandartChip } from '@/components/detail/VersandartChip';
import { UeberfaelligBadge } from '@/components/detail/UeberfaelligBadge';
import { FortschrittBadge } from '@/components/detail/FortschrittBadge';
import { PositionenSektion } from '@/components/detail/PositionenSektion';
import { abschlussWeg, bereitZielZustand } from '@/lib/zustandLabel';
import { heuteIso } from '@/lib/format';
import { useZustaende } from '@/hooks/useZustaende';
import { ChecklistSektion } from '@/components/detail/ChecklistSektion';
import { SendungenSektion } from '@/components/detail/SendungenSektion';
import { ArchivKnopf } from '@/components/detail/ArchivKnopf';
import { SendungDialog } from '@/components/detail/SendungDialog';
import { KommunikationSektion } from '@/components/detail/KommunikationSektion';
import { NotizSektion } from '@/components/detail/NotizSektion';
import { AuditLogSektion } from '@/components/detail/AuditLogSektion';
import { ZuordnungSektion } from '@/components/detail/ZuordnungSektion';
import { C } from '@/theme';
import { useZurueck } from '@/hooks/useZurueck';
import { useZeigerFein } from '@/hooks/useLayout';
import { CODE_ABHOLUNG } from '@/hooks/useVersandarten';
import { VerlassenWarnung } from '@/components/detail/VerlassenWarnung';
import {
  allesEingegangen as istAllesEingegangen,
  verlassenWarnungArt,
  verlassenWarnungMerken,
  verlassenWarnungSchonGezeigt
} from '@/lib/verlassenWarnung';
import type { Bestellung, KundePatch, LieferadressePatch, ZustandPatch, AbholdatumPatch, WiedervorlagePatch, NotizPatch, ChecklistPatch, Lieferwunsch } from '@/types/detail';
import { bestellungGeoeffnet, melde } from '@/lib/nutzung';
import { kopfBestellungSetzen, kopfSichtbarSetzen, kopfZurueckSetzen } from '@/lib/kopfKontext';

type Versandart = { id: number; code: string; label: string };

function BestellungDetail() {
  const { bestellnr } = Route.useParams();
  // Zurück dorthin, woher man kam — Suche, Brett, Archiv, Reserviert?
  // (hooks/useZurueck.ts). Ohne Vorgänger: Dashboard.
  const zurueck = useZurueck({ to: '/' });
  const q = useBestellung(bestellnr);
  const toast = useToast();
  const qc = useQueryClient();
  const [abholungLaeuft, setAbholungLaeuft] = useState(false);

  // Nutzungsmessung: die Uhr der Dashboard-Suche anhalten, und merken,
  // ob die Bestellung binnen zehn Sekunden wieder verlassen wird — dann
  // zeigte die Liste wohl das Falsche.
  useEffect(() => {
    bestellungGeoeffnet();
    const start = performance.now();
    return () => {
      if (performance.now() - start < 10_000) melde('sofort_zurueck', { ort: 'bestellung' });
    };
  }, [bestellnr]);

  // Obere Leiste (03.11.2026, Skizze B): Ist der Kopf mit Nummer und
  // Kunde weggescrollt, zeigt die Leiste beides samt Zustand. Gemeldet
  // wird hier, gelesen in Topbar.tsx (lib/kopfKontext.ts).
  const kopfB = q.data?.bestellung;
  const kopfNr = kopfB?.bestellnr;
  const kopfKunde = kopfB?.kunde.anzeigename;
  const kopfZustand = kopfB?.zustand.label ?? null;
  useEffect(() => {
    kopfBestellungSetzen(kopfNr ? { bestellnr: kopfNr, kunde: kopfKunde ?? '', zustand: kopfZustand } : null);
  }, [kopfNr, kopfKunde, kopfZustand]);
  useEffect(() => () => kopfBestellungSetzen(null), []);
  // Der Zurück-Weg für die Leiste (E63): dieselbe Funktion wie der Knopf
  // unten auf der Seite — sie wird nur weitergereicht.
  useEffect(() => {
    kopfZurueckSetzen(zurueck);
    return () => kopfZurueckSetzen(null);
  }, [zurueck]);
  const kopfBeobachter = useRef<IntersectionObserver | null>(null);
  const kopfRef = useCallback((el: HTMLDivElement | null) => {
    kopfBeobachter.current?.disconnect();
    kopfBeobachter.current = null;
    if (el === null || typeof IntersectionObserver === 'undefined') {
      kopfSichtbarSetzen(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => kopfSichtbarSetzen(e.isIntersecting));
    io.observe(el);
    kopfBeobachter.current = io;
  }, []);

  // Die Nachfrage beim Verlassen einer fertigen Bestellung
  // (lib/verlassenWarnung.ts). Der Blocker hängt sich an jede Navigation
  // — den Zurück-Knopf, die Seitenleiste, die Browser-Taste — und hält
  // sie an, bis eine der drei Antworten fällt.
  const zeigerFein = useZeigerFein();
  const warnungArt = verlassenWarnungArt(q.data?.bestellung, zeigerFein);
  const [warnungLaeuft, setWarnungLaeuft] = useState(false);
  const blocker = useBlocker({
    // Nur beim VERLASSEN der Bestellung (anderer Pfad). Ein Blatt über der
    // Seite — „Reserviert?" öffnet sich über einen Suchparameter — ist kein
    // Verlassen; bis 02.11.2026 hielt die Warnung auch das an.
    shouldBlockFn: ({ current, next }) =>
      next.pathname !== current.pathname &&
      warnungArt !== null &&
      !verlassenWarnungSchonGezeigt(bestellnr),
    disabled: warnungArt === null,
    enableBeforeUnload: false,
    withResolver: true
  });

  const versandarten = useQuery({
    queryKey: ['lookups', 'versandarten'],
    queryFn: () =>
      apiFetch<{ type: string; items: Versandart[] }>('/lookups.php?type=versandarten'),
    staleTime: 10 * 60_000
  });

  const patchZustand = usePatchBestellung<ZustandPatch>({
    path: '/bestellungen/zustand.php',
    bestellnr,
    showSuccessToast: false
  });
  const patchAbholdatum = usePatchBestellung<AbholdatumPatch>({
    path: '/bestellungen/abholdatum.php',
    bestellnr,
    // Ein geleertes Feld ist ein Entfernen, kein Speichern (A9).
    //
    // "Termin", nicht "Abholdatum": die Rückmeldung blitzt direkt neben
    // dem Feld auf, das im Kopf "Termin:" heißt, und nennt dieselbe
    // Sache. Sie war nach B110/B111 die letzte Stelle mit dem alten Wort.
    erfolgsText: (b) =>
      b.abholdatum_zugesagt === null ? 'Termin entfernt.' : 'Termin gespeichert.'
  });
  const patchWiedervorlage = usePatchBestellung<WiedervorlagePatch>({
    path: '/bestellungen/wiedervorlage.php',
    bestellnr,
    erfolgsText: 'Wiedervorlage gespeichert.'
  });
  const patchVersandart = usePatchBestellung<{ id: number; versandart_id: number }>({
    path: '/bestellungen/versandart.php',
    bestellnr,
    erfolgsText: 'Versandart gespeichert.'
  });
  // A30.1: Der Geschenkwunsch laesst sich nachtragen. Kein
  // `mitBestaetigung` — es ist eine Angabe, kein Vorgang, und sofort
  // wieder umschaltbar.
  const patchGeschenk = usePatchBestellung<{ id: number; ist_geschenk: boolean }>({
    path: '/bestellungen/geschenk.php',
    bestellnr,
    erfolgsText: (b) =>
      b.ist_geschenk ? 'Als Geschenk vermerkt.' : 'Geschenkwunsch entfernt.'
  });
  // A56 (#135): der Lieferwunsch. Wie der Geschenkwunsch ohne
  // `mitBestaetigung` — eine Angabe, kein Vorgang.
  const patchLieferwunsch = usePatchBestellung<{ id: number; lieferwunsch: Lieferwunsch }>({
    path: '/bestellungen/lieferwunsch.php',
    bestellnr,
    erfolgsText: (b) =>
      b.lieferwunsch === 'sofort'
        ? 'Teillieferung ist erwünscht.'
        : 'Erst wenn alles da ist.'
  });
  const patchNotiz = usePatchBestellung<NotizPatch>({
    path: '/bestellungen/notiz.php',
    bestellnr,
    erfolgsText: 'Notiz gespeichert.'
  });
  const patchLieferadresse = usePatchBestellung<LieferadressePatch>({
    path: '/bestellungen/lieferadresse.php',
    bestellnr,
    erfolgsText: 'Lieferadresse gespeichert.'
  });
  const patchKunde = usePatchBestellung<KundePatch>({
    path: '/bestellungen/kunde.php',
    bestellnr,
    erfolgsText: 'Kundenangaben gespeichert.'
  });
  const patchChecklist = usePatchBestellung<ChecklistPatch>({
    path: '/bestellungen/checklist.php',
    bestellnr,
    showSuccessToast: false,
    optimistic: {
      queryKey: ['bestellung', bestellnr],
      apply: (cache, body) => {
        if (!cache || typeof cache !== 'object') return cache;
        const c = cache as { bestellung: Bestellung };
        return {
          ...c,
          bestellung: {
            ...c.bestellung,
            checklist: c.bestellung.checklist.map((cl) =>
              cl.code === body.feld ? { ...cl, erledigt: body.wert } : cl
            )
          }
        };
      }
    }
  });

  const { confirm, dialog: bestaetigungsDialog } = useConfirm();
  // Für die Zustandsnamen im Verlauf (A11.2); derselbe Query-Schlüssel
  // wie im ZustandSwitcher, also kein zweiter Abruf.
  const zustaende = useZustaende();
  const seriell = useSerielleAktion();
  const sendungCreate = useCreateSendung(bestellnr);
  const [sendungDialog, setSendungDialog] = useState(false);
  // Derselbe Dialog, zwei Wege hinein (A7.4 / #50):
  //   'bereit'      ueber „Alle eingetroffen — bereit setzen": Sendung
  //                 anlegen UND Zustand setzen.
  //   'nur_sendung' ueber „Sendung erstellen" an der Sendungs-Sektion:
  //                 nur die Sendung, der Zustand bleibt.
  const [sendungModus, setSendungModus] = useState<'bereit' | 'nur_sendung'>('bereit');
  // Serverfehler halten den Dialog offen, damit die Eingaben nicht
  // verlorengehen (B18).
  const [sendungFehler, setSendungFehler] = useState<string | null>(null);

  if (q.isPending) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  // Eine Nummer, die es nicht gibt, ist kein Fehler des Systems, sondern
  // ein Vertipper oder ein alter Zettel. Sie bekommt deshalb eine eigene
  // Ansicht mit Weg zurueck — vorher stand hier fuer beide Faelle
  // derselbe rote Satz, ohne dass man weiterkam (B97).
  const nichtGefunden = q.isError && q.error instanceof ApiError && q.error.status === 404;
  if (nichtGefunden || q.isError || !q.data) {
    return (
      <div className="flex flex-col items-start gap-3 p-8">
        <h2 className="font-serif text-lg text-ink">
          {nichtGefunden ? 'Bestellung nicht gefunden' : 'Bestellung konnte nicht geladen werden'}
        </h2>
        <p className="text-sm text-ink-soft">
          {nichtGefunden
            ? `Zur Nummer ${bestellnr} gibt es keine Bestellung. Vertippt, oder sie wurde inzwischen entfernt?`
            : 'Beim Laden ist etwas schiefgegangen. Versuch es noch einmal.'}
        </p>
        <div className="flex gap-2">
          <Button variant="primary" onClick={zurueck}>
            <ArrowLeft className="h-4 w-4" />
            Zurück
          </Button>
          {!nichtGefunden && (
            <Button variant="secondary" onClick={() => void q.refetch()}>
              Erneut versuchen
            </Button>
          )}
        </div>
      </div>
    );
  }

  const b = q.data.bestellung;

  // Bereits in Sendungen verplante Menge je Position (B24).
  const verplant = new Map<number, number>();
  for (const s of b.sendungen) {
    for (const sp of s.positionen) {
      verplant.set(sp.position_id, (verplant.get(sp.position_id) ?? 0) + sp.menge);
    }
  }
  const offenVon = (id: number, menge: number): number =>
    Math.max(0, menge - (verplant.get(id) ?? 0));
  const etwasOffen = b.positionen.some((p) => offenVon(p.id, p.menge_wirksam) > 0);

  // Weiche Sperre fuer Endzustaende (B15): keine harte Blockade, aber jede
  // Aenderung will einmal bestaetigt werden. Bewusst pro Aktion und ohne
  // Dauerbanner.
  async function bestaetigeAenderung(): Promise<boolean> {
    if (!b.zustand.ist_endzustand) return true;
    return confirm({
      titel: `Bestellung ist ${b.zustand.label}`,
      text: 'Diese Bestellung ist in einem Endzustand. Änderung trotzdem vornehmen?',
      bestaetigenLabel: 'Trotzdem ändern'
    });
  }

  // Kapselt eine Mutation hinter der Endzustands-Rueckfrage.
  function mitBestaetigung(aktion: () => void): void {
    void bestaetigeAenderung().then((ok) => {
      if (ok) aktion();
    });
  }

  // Variante, die zurueckmeldet, ob wirklich gespeichert wurde. Formulare
  // mit Entwurfs-Sicherung muessen das wissen: bei abgelehnter Rueckfrage
  // darf der Entwurf nicht weggeworfen werden (S-1).
  async function mitBestaetigungAsync(aktion: () => Promise<unknown>): Promise<boolean> {
    if (!(await bestaetigeAenderung())) return false;
    await aktion();
    return true;
  }

  /**
   * Der Knopf „Alle eingetroffen …" an der Positionen-Sektion (A11.1).
   *
   * DER WEG IST ASYMMETRISCH
   *   Bei Abholung fuehrt er auf `bereit` und oeffnet den
   *   Sendungs-Dialog, damit die Wahl sichtbar bleibt (B56) — danach
   *   kommt die Kundin, und jemand markiert abgeholt.
   *
   *   Bei Versand fuehrt er direkt auf `abgeschlossen`, ohne Dialog.
   *   Dahinter kommt kein Handgriff mehr: die Bestellung geht zum
   *   Packtisch. Ein Sendungs-Dialog wuerde nach einem Versanddatum und
   *   einer Sendungsnummer fragen, die niemand nachtraegt — die
   *   Sendungsverfolgung liegt bei DPD und der Post, nicht bei uns
   *   (Designprinzip 1). Wer im Einzelfall doch eine Sendung anlegen
   *   will, hat dafuer weiterhin „Sendung erstellen".
   *
   *   Bewusst NICHT ueber handleZustand: das legt bei vollstaendigem
   *   Eingang und gesetzter Versandart sofort eine Sendung an.
   */
  function handleBereitKnopf(): void {
    mitBestaetigung(() => {
      const ziel = bereitZielZustand(b.versandart?.code ?? null);
      if (ziel === 'abgeschlossen') {
        patchZustand.mutate({ id: b.id, zustand_code: 'abgeschlossen' });
        return;
      }
      // Abholung, alles da: ein Tipp, kein Dialog (02.11.2026). Der
      // Knopf steht nur, wenn alles eingetroffen ist — die einzige echte
      // Wahl („mitgeben oder warten") stellt sich erst bei Teil-Eingang,
      // und der geht über die Zustandsauswahl (handleZustand).
      if (b.versandart?.code === CODE_ABHOLUNG && istAllesEingegangen(b.positionen)) {
        void abholbereitStill();
        return;
      }
      // E55: Glomer — kein Sendungs-Dialog; die Sendung kommt aus Shopify.
      if (b.shopify_order_id !== null) {
        void abholbereitStill();
        return;
      }
      setSendungModus('bereit');
      setSendungFehler(null);
      setSendungDialog(true);
    });
  }

  /**
   * „Abholbereit" ohne Dialog: Zustand setzen und die Abhol-Sendung mit
   * allem, was noch nicht verplant ist, still anlegen. Der Geschenkwunsch
   * der Bestellung wird übernommen (E30/E41) — vorher stand dafür ein
   * Häkchen im Dialog, das man hätte setzen müssen. Ist schon alles
   * verplant, bleibt nur der Zustand.
   */
  async function abholbereitStill(): Promise<void> {
    await patchZustand.mutateAsync({ id: b.id, zustand_code: 'bereit' });
    // E55: Bei Glomer legt Shopify die Sendung an (Ausführung dort).
    if (!etwasOffen || b.shopify_order_id !== null) return;
    await sendungCreate.mutateAsync({
      bestellung_id: b.id,
      modus: 'vollstaendig',
      ist_geschenk: b.ist_geschenk === true
    });
    toast('success', b.ist_geschenk ? 'Abholbereit — als Geschenk verpacken.' : 'Abholbereit.');
  }

  /** Antworten auf die Nachfrage beim Verlassen (lib/verlassenWarnung.ts). */
  async function warnungSchritt(): Promise<void> {
    if (warnungLaeuft) return;
    setWarnungLaeuft(true);
    try {
      if (warnungArt === 'abholung') {
        await abholbereitStill();
      } else {
        await patchZustand.mutateAsync({ id: b.id, zustand_code: 'abgeschlossen' });
      }
      verlassenWarnungMerken(bestellnr);
      blocker.proceed?.();
    } catch (err) {
      toast('error', `Konnte nicht gespeichert werden: ${err instanceof ApiError ? err.code : 'unbekannt'}`);
      blocker.reset?.();
    } finally {
      setWarnungLaeuft(false);
    }
  }
  function warnungSpaeter(): void {
    melde('warnung_spaeter', { ort: warnungArt ?? undefined });
    verlassenWarnungMerken(bestellnr);
    blocker.proceed?.();
  }
  function warnungBleiben(): void {
    verlassenWarnungMerken(bestellnr);
    blocker.reset?.();
  }

  function handleZustand(code: string): void {
    // Glomer mit Versand: den Abschluss meldet Shopify (E47). Von Hand
    // bleibt er moeglich — fuer den Fall, dass eine Bestellung ausserhalb
    // von Shopify erledigt wurde —, aber nicht ohne Rueckfrage.
    if (code === 'abgeschlossen' && abschlussWeg(b) === 'shopify' && !b.zustand.ist_endzustand) {
      void confirm({
        titel: 'Glomer-Bestellung',
        text:
          'Glomer-Bestellungen schließen hier von selbst ab, sobald sie in Shopify ausgeführt sind. ' +
          'Nur von Hand abschließen, wenn sie in Shopify nicht ausgeführt wird.',
        bestaetigenLabel: 'Trotzdem abschließen'
      }).then((ok) => {
        if (ok) patchZustand.mutate({ id: b.id, zustand_code: code });
      });
      return;
    }
    // Auch das Herauswechseln aus einem Endzustand will bestaetigt sein (B15).
    mitBestaetigung(() => {
      // E55: Glomer — der Zustand ja, eine Sendung nein: Die kommt aus
      // der Ausführung in Shopify.
      if (code === 'bereit' && b.shopify_order_id !== null) {
        patchZustand.mutate({ id: b.id, zustand_code: 'bereit' });
        return;
      }
      if (code === 'bereit') {
        setSendungModus('bereit');
        // Eingetroffen heisst vollstaendig eingetroffen (B23).
        // Gegen die wirksame Menge (Migration 035).
        const allesEingegangen =
          b.positionen.some((p) => p.menge_wirksam > 0) &&
          b.positionen.every((p) => p.menge_eingegangen >= p.menge_wirksam);
        if (allesEingegangen && b.versandart !== null && etwasOffen) {
          patchZustand.mutate(
            { id: b.id, zustand_code: 'bereit' },
            {
              onSuccess: () =>
                sendungCreate.mutate({
                  bestellung_id: b.id,
                  modus: 'vollstaendig',
                  ist_geschenk: b.ist_geschenk === true
                })
            }
          );
          return;
        }
        setSendungFehler(null);
        setSendungDialog(true);
        return;
      }
      patchZustand.mutate({ id: b.id, zustand_code: code });
    });
  }

  /**
   * „Abgeholt" (Konzeptpruefung 29.10.2026, E48).
   *
   * Gemessen: Von 24 abgeschlossenen Abholungen liefen 18 ueber die
   * Zustandsauswahl, 3 ueber „Abgeholt markieren" unten an der Sendung —
   * der Knopf war zu weit weg. 64 Bestellungen standen auf
   * „Abholbereit", die aelteste seit 51 Tagen. Jetzt steht der Griff
   * neben dem Zustand.
   *
   * Was er tut, ist dasselbe wie bisher, nur ohne Umweg: Offene
   * Abhol-Sendungen werden mit heutigem Datum gebucht (der Server
   * schliesst dann ab, wenn nichts mehr offen ist, E25). Gibt es keine —
   * „Abholbereit" wurde von Hand gesetzt —, wird der Zustand direkt
   * gesetzt.
   */
  async function handleAbgeholt(): Promise<void> {
    if (abholungLaeuft) return;
    setAbholungLaeuft(true);
    try {
      const offen = b.sendungen.filter(
        (s) => s.versandart?.code === 'abholung' && s.abgeholt_am === null
      );
      if (offen.length > 0) {
        for (const s of offen) {
          await apiFetch('/sendungen/abgeholt.php', {
            method: 'POST',
            body: JSON.stringify({ id: s.id, abgeholt_am: heuteIso() })
          });
        }
        await qc.invalidateQueries({ queryKey: ['bestellung', bestellnr] });
      } else {
        await patchZustand.mutateAsync({ id: b.id, zustand_code: 'abgeschlossen' });
      }
      toast('success', 'Als abgeholt markiert.');
    } catch (err) {
      toast('error', `Konnte nicht gespeichert werden: ${err instanceof ApiError ? err.code : 'unbekannt'}`);
    } finally {
      setAbholungLaeuft(false);
    }
  }

  function fehlertext(err: unknown): string {
    const code = err instanceof ApiError ? err.code : 'unbekannt';
    if (code === 'alle_positionen_verplant') {
      return 'Alle Positionen sind bereits Sendungen zugeordnet.';
    }
    if (code === 'keine_eingetroffenen_positionen') {
      return 'Keine vollständig eingetroffene Position vorhanden.';
    }
    if (code === 'versandart_required') return 'Versandart fehlt — bitte wählen.';
    // 'adresse_unvollstaendig' gibt es seit E8 nicht mehr: die fehlende
    // Anschrift ist ein Hinweis im Sendungsdialog und kein Abbruch. Die
    // Zuordnung hier ersatzlos zu streichen ist Absicht — ein Text für
    // einen Fehler, den niemand mehr sendet, führte beim nächsten Lesen
    // in die Irre.
    return `Sendung fehlgeschlagen: ${code}`;
  }

  async function handleSendungDialogConfirm(
    modus: 'vollstaendig' | 'teil_eingetroffen' | null,
    versandartId: number | null,
    istGeschenk: boolean,
    // A46: Bei Versand geht die Sendung mit dem Anlegen hinaus. Bei
    // Abholung kommt hier zweimal null an.
    versandDatum: string | null,
    trackingNr: string | null
  ): Promise<void> {
    setSendungFehler(null);
    const setztZustand = sendungModus === 'bereit';
    try {
      if (modus === null) {
        // „Trotzdem als bereit markieren ohne Sendung" gibt es nur auf
        // dem Weg ueber den Zustand; ohne ihn kann dieser Fall gar nicht
        // eintreten.
        if (setztZustand) {
          await patchZustand.mutateAsync({ id: b.id, zustand_code: 'bereit' });
        }
        setSendungDialog(false);
        return;
      }
      // Nur vollstaendig eingetroffene und noch nicht verplante Positionen (B23/B24).
      const positionen = b.positionen
        .filter((p) => p.menge_eingegangen >= p.menge_wirksam && offenVon(p.id, p.menge_wirksam) > 0)
        .map((p) => ({ position_id: p.id, menge: offenVon(p.id, p.menge_wirksam) }));
      if (positionen.length === 0) {
        setSendungFehler('Keine vollständig eingetroffene, offene Position vorhanden.');
        return;
      }
      // DER PUNKT VON #50: Auf dem Weg „Sendung erstellen" wird der
      // Zustand nicht angefasst. Eine Teilsendung fuer eine Bestellung,
      // die noch nicht fertig ist, darf sie nicht als bereit ausgeben.
      if (setztZustand) {
        await patchZustand.mutateAsync({ id: b.id, zustand_code: 'bereit' });
      }
      await sendungCreate.mutateAsync({
        bestellung_id: b.id,
        modus,
        versandart_id: versandartId ?? undefined,
        positionen,
        ist_geschenk: istGeschenk,
        ...(versandDatum === null ? {} : { versand_datum: versandDatum }),
        ...(trackingNr === null ? {} : { tracking_nr: trackingNr })
      });
      setSendungDialog(false);
    } catch (err) {
      // Dialog bleibt offen, Eingaben erhalten (B18).
      setSendungFehler(fehlertext(err));
    }
  }

  // "Auf vollstaendigen Eingang warten" aendert bewusst nichts (B19).
  function handleWarten(): void {
    setSendungDialog(false);
    setSendungFehler(null);
    toast('success', 'Zustand bleibt unverändert.');
  }

  return (
    // Alle Sektionen stehen untereinander in voller Breite — eine
    // Spalte, vom Telefon bis zum Schreibtisch. Am Telefon nur mit
    // schmalerem Rand: 2 × 16 Pixel sind dort ein Zwanzigstel der
    // nutzbaren Breite.
    <div className="mx-auto w-full max-w-5xl space-y-4 p-3 sm:space-y-5 sm:p-4">
      <button
        type="button"
        onClick={zurueck}
        className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-ink-soft hover:text-akzent"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück
      </button>

      {bestaetigungsDialog}
      {warnungArt !== null && (
        <VerlassenWarnung
          offen={blocker.status === 'blocked'}
          art={warnungArt}
          laeuft={warnungLaeuft}
          onSchritt={() => void warnungSchritt()}
          onSpaeter={warnungSpaeter}
          onBleiben={warnungBleiben}
        />
      )}

      <div ref={kopfRef} data-testid="bestellung-kopf">
        <DetailHeader
          bestellung={b}
          onLieferadresseSave={(p) => mitBestaetigung(() => patchLieferadresse.mutate(p))}
          onKundeSave={(p) => mitBestaetigung(() => patchKunde.mutate(p))}
        />
      </div>

      {/* Die Notiz zur Bestellung steht GANZ OBEN, direkt unter Nummer und
          Kunde (Betreiber, 02.11.2026: „erste Priorität"). Unten, unter
          Kommunikation, las sie niemand rechtzeitig — dabei steht dort,
          was man wissen muss, bevor man irgendetwas anfasst. */}
      <NotizSektion
        bestellungId={b.id}
        value={b.notiz}
        onSave={(n) =>
          mitBestaetigungAsync(() => patchNotiz.mutateAsync({ id: b.id, notiz: n }))
        }
      />

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-cream px-4 py-3">
        <span data-tour="detail-zustand">
          <ZustandSwitcher
            current={b.zustand}
            versandartCode={b.versandart?.code ?? null}
            onSelect={handleZustand}
          />
        </span>
        {abschlussWeg(b) === 'abholung' && b.zustand.code === 'bereit' && (
          <Button data-nutzung="abgeholt" variant="primary" onClick={() => void handleAbgeholt()} disabled={abholungLaeuft}>
            <PackageCheck className="h-4 w-4" />
            Abgeholt
          </Button>
        )}
        <UeberfaelligBadge
          zugesagt={b.abholdatum_zugesagt}
          istEndzustand={b.zustand.ist_endzustand}
          restOffen={b.fortschritt?.offen ?? null}
        />
        {/* A57.1 (#123): „2 von 3 da" im Kopf — dieselbe Marke wie am
            Brett, damit man beim Öffnen nicht neu suchen muss. Sie
            steht neben dem Zustand, weil sie ihn erklärt: Der Zustand
            folgt der langsamsten Position, die Marke sagt, wie weit
            die schnelleren sind. */}
        <FortschrittBadge fortschritt={b.fortschritt} />
        <VersandartChip
          aktuell={b.versandart}
          gesperrt={b.shopify_order_id !== null}
          optionen={versandarten.data?.items ?? []}
          onSelect={(vid) =>
            mitBestaetigung(() => patchVersandart.mutate({ id: b.id, versandart_id: vid }))
          }
        />
        <AbholdatumEditor
          value={b.abholdatum_zugesagt}
          onSave={(d) =>
            mitBestaetigung(() => patchAbholdatum.mutate({ id: b.id, abholdatum_zugesagt: d }))
          }
        />
        {/* A30.1: Der Griff sitzt bei den uebrigen aenderbaren Angaben und
            steht auch dann da, wenn kein Wunsch gesetzt ist — das
            Merkzeichen im Kopf erscheint nur im gesetzten Fall und kann
            deshalb nicht der Weg zum Setzen sein. */}
        <GeschenkSchalter
          wert={b.ist_geschenk}
          onChange={(neu) => patchGeschenk.mutate({ id: b.id, ist_geschenk: neu })}
        />
        {/* A56 (#135): Der Lieferwunsch steht dort, wo entschieden wird,
            ob etwas rausgeht — nicht in einem Aufklappbereich vergraben. */}
        <LieferwunschSchalter
          wert={b.lieferwunsch}
          nachrichtWunsch={b.nachricht_wunsch}
          onChange={(neu) => patchLieferwunsch.mutate({ id: b.id, lieferwunsch: neu })}
        />
        <WiedervorlageEditor
          datum={b.wiedervorlage_am}
          grund={b.wiedervorlage_grund}
          onSave={(d, g) =>
            mitBestaetigung(() =>
              patchWiedervorlage.mutate({
                id: b.id,
                wiedervorlage_am: d,
                wiedervorlage_grund: g
              })
            )
          }
        />
        {/* E13/A10.6: nur im Endzustand, und nur solange sie noch im
            Brett steht. Der Knopf zieht die Wartezeit vor, mehr nicht. */}
        <ArchivKnopf
          bestellungId={b.id}
          bestellnr={b.bestellnr}
          istEndzustand={b.zustand.ist_endzustand}
          archiviertAm={b.archiviert_am}
        />

        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-ink-faint">
          {/* Auch hier der Farbpunkt: dieselbe Angabe traegt im Dashboard
              (ErfasserBadge) und im Erfassungs-Blatt laengst eine Farbe,
              nur der Kopf der Bestellung nicht. */}
          Erfasser:
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: b.erfasser.farbe ?? C.inkFaint }}
          />
          {b.erfasser.kuerzel} · {b.erfasser.name}
        </span>
      </div>

      <PositionenSektion
        anker="detail-positionen"
        bestellung={b}
        bestaetigeAenderung={bestaetigeAenderung}
        onBereitSetzen={handleBereitKnopf}
      />

      <ChecklistSektion
        anker="detail-checkliste"
        bestellnr={b.bestellnr}
        bestellungId={b.id}
        eintraege={b.checklist}
        // A56.5 (#135): Der Punkt „Kunde benachrichtigt" bleibt IMMER
        // sichtbar und bekommt nur einen Zusatz, wenn ein Wunsch
        // vermerkt ist.
        nachrichtWunsch={b.nachricht_wunsch}
        nachrichtTitel={b.positionen.filter((p) => p.nachricht_wunsch).map((p) => p.titel)}
        // Serialisiert, damit sich schnelle Klicks nicht gegenseitig
        // ueberholen (B5).
        onToggle={(feld, wert) =>
          mitBestaetigung(() =>
            void seriell(() => patchChecklist.mutateAsync({ id: b.id, feld, wert }))
          )
        }
      />

      <SendungenSektion
        anker="detail-sendungen"
        bestellung={b}
        bestaetigeAenderung={bestaetigeAenderung}
        onSendungErstellen={() =>
          mitBestaetigung(() => {
            setSendungModus('nur_sendung');
            setSendungFehler(null);
            setSendungDialog(true);
          })
        }
      />

      {/* A52 (#133): steht zwischen Sendungen und Kommunikation, weil
          es dort um dieselbe Frage geht — was ist mit dieser Ware
          passiert, und wo steht die Sendungsnummer. */}
      <ZuordnungSektion anker="detail-zuordnung" bestellung={b} />

      <KommunikationSektion anker="detail-kommunikation" bestellung={b} />

      <AuditLogSektion
        bestellungId={b.id}
        eintraege={b.audit_log_letzte}
        zustandKontext={{
          versandartCode: b.versandart?.code ?? null,
          zustaende: zustaende.data?.items ?? []
        }}
      />

      <SendungDialog
        open={sendungDialog}
        mitZustand={sendungModus === 'bereit'}
        bestellung={b}
        versandarten={versandarten.data?.items ?? []}
        isPending={patchZustand.isPending || sendungCreate.isPending}
        fehler={sendungFehler}
        onConfirm={(m, va, g, d, t) => void handleSendungDialogConfirm(m, va, g, d, t)}
        onWarten={handleWarten}
        onCancel={() => {
          setSendungDialog(false);
          setSendungFehler(null);
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/bestellungen/$bestellnr')({
  component: BestellungDetail
});
